import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Globe, ArrowRight, Sparkles } from 'lucide-react';
import { AIConcierge } from '../components/ai/AIConcierge';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  // Individuals skip onboarding — they don't need virtual office setup
  React.useEffect(() => {
    if (user?.role === 'individual') {
      navigate('/deals');
    }
  }, [user, navigate]);

  const handleComplete = async (suggestions) => {
    try {
      // Create virtual office if suggested
      if (suggestions?.suggested_office_name) {
        await api.post('/virtual-offices', {
          name: suggestions.suggested_office_name,
          industry: suggestions.industry || 'manufacturing',
          languages: suggestions.languages || ['en'],
          document_checklist: suggestions.document_checklist || []
        });
        toast.success(t('onboarding.toast.officeCreated'));
      }

      // Update user profile if needed
      if (suggestions?.role) {
        await api.put('/auth/me', {
          preferred_language: suggestions.languages?.[0] || 'en'
        });
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('Setup error:', error);
      navigate('/dashboard');
    }
  };

  const skipOnboarding = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen hero-mist">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center">
            <img src="/assets/pocketfiler-header-logo.png" alt="PocketFiler" className="h-10" />
          </div>
          <Button variant="ghost" onClick={skipOnboarding} data-testid="skip-onboarding-button">
            {t('onboarding.skip')}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* Main content */}
        <div className="grid lg:grid-cols-2 gap-8 items-start max-w-6xl mx-auto">
          {/* Left - Welcome */}
          <div className="space-y-6 pt-8 lg:pt-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
              <Sparkles className="h-4 w-4" />
              {t('onboarding.badge')}
            </div>
            <h1 className="text-3xl lg:text-4xl font-display font-bold">
              {user?.name ? t('onboarding.welcome', { name: user.name }) : t('onboarding.welcomeGeneric')} 👋
            </h1>
            <p className="text-lg text-muted-foreground">
              {t('onboarding.subtitle')}
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-semibold">1</span>
                </div>
                <span>{t('onboarding.step1')}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-semibold">2</span>
                </div>
                <span>{t('onboarding.step2')}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-primary font-semibold">3</span>
                </div>
                <span>{t('onboarding.step3')}</span>
              </div>
            </div>
          </div>

          {/* Right - AI Concierge */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                {t('onboarding.concierge.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <AIConcierge
                onComplete={handleComplete}
                initialContext={{ user }}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
